import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from './ui/button';
import { 
  FileText, 
  Scissors, 
  Merge, 
  Home, 
  ChevronDown,
  Menu,
  Wrench,
  Image,
  Archive,
  Maximize2,
  Lock,
  Type,
  Search,
  Eraser,
  MessageSquare,
  ExternalLink,
  MoreHorizontal
} from 'lucide-react';
import { cn } from '../lib/utils';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import FeedbackModal from './FeedbackModal';

const Navbar = () => {
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [feedbackModalOpen, setFeedbackModalOpen] = useState(false);

  const scrollToMoreTools = () => {
    if (location.pathname === '/') {
      requestAnimationFrame(() => {
        document.getElementById('json-tools')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    }
  };

  const pdfTools = [
    { path: '/split-pdf', label: 'Split PDF', icon: Scissors },
    { path: '/merge-pdf', label: 'Merge PDF', icon: Merge },
    { path: '/compress-pdf', label: 'Compress PDF', icon: Archive },
    { path: '/lock-unlock-pdf', label: 'Lock/Unlock PDF', icon: Lock }
  ];

  const imageTools = [
    { path: '/convert-image', label: 'Convert Image', icon: Image },
    { path: '/resize-image', label: 'Resize Image', icon: Maximize2 },
    { path: '/compress-image', label: 'Compress Image', icon: Archive },
    { path: '/remove-background', label: 'Remove Background', icon: Eraser }
  ];

  const textTools = [
    { path: '/text-case-converter', label: 'Case Converter', icon: Type },
    { path: '/text-find-replace', label: 'Find and Replace', icon: Search }
  ];

  const isPdfToolActive = pdfTools.some(tool => location.pathname === tool.path);
  const isImageToolActive = imageTools.some(tool => location.pathname === tool.path);
  const isTextToolActive = textTools.some(tool => location.pathname === tool.path);

  return (
    <nav className="bg-white border-b border-gray-200 shadow-sm">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2 sm:space-x-3">
            <img 
              src="/logo.png" 
              alt="AllSmallTooLS Logo" 
              className="h-8 w-8 sm:h-10 sm:w-10"
            />
            <div className="flex flex-col">
              <span className="text-base sm:text-xl font-bold text-gray-900">AllSmallTooLS</span>
              <span 
                className="text-[9px] sm:text-[10px] text-gray-500 hover:text-indigo-600 transition-colors -mt-1 whitespace-nowrap cursor-pointer"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  window.open('https://www.litspark.solutions/?utm_source=allsmalltools', '_blank', 'noopener,noreferrer');
                }}
              >
                Powered by LitSpark
              </span>
            </div>
          </Link>

          {/* Mobile Hamburger Button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="lg:hidden p-2 rounded-md text-gray-700 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-600"
            aria-label="Toggle menu"
          >
            <Menu className="h-6 w-6" />
          </button>

          {/* Desktop Navigation Links */}
          <div className="hidden lg:flex items-center space-x-1">
            {/* Home/Dashboard */}
            <Link to="/">
              <Button
                variant={location.pathname === '/' ? "default" : "ghost"}
                className={cn(
                  "flex items-center space-x-2 transition-all duration-200",
                  location.pathname === '/' && "bg-indigo-600 text-white shadow-md"
                )}
              >
                <Home className="h-4 w-4" />
                <span className="hidden sm:inline">Home</span>
              </Button>
            </Link>

            {/* Online Notepad */}
            <Link to="/online-notepad">
              <Button
                variant={location.pathname === '/online-notepad' ? "default" : "ghost"}
                className={cn(
                  "flex items-center space-x-2 transition-all duration-200",
                  location.pathname === '/online-notepad' && "bg-indigo-600 text-white shadow-md"
                )}
              >
                <FileText className="h-4 w-4" />
                <span className="hidden sm:inline">Online Notepad</span>
              </Button>
            </Link>

            {/* PDF Tools Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant={isPdfToolActive ? "default" : "ghost"}
                  className={cn(
                    "flex items-center space-x-2 transition-all duration-200",
                    isPdfToolActive && "bg-indigo-600 text-white shadow-md"
                  )}
                >
                  <FileText className="h-4 w-4" />
                  <span className="hidden sm:inline">PDF</span>
                  <ChevronDown className="h-3 w-3 ml-1" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                {pdfTools.map((tool) => {
                  const IconComponent = tool.icon;
                  const isActive = location.pathname === tool.path;
                  
                  return (
                    <DropdownMenuItem key={tool.path} asChild>
                      <Link 
                        to={tool.path}
                        className={cn(
                          "flex items-center space-x-2 w-full px-3 py-2 transition-colors",
                          isActive && "bg-indigo-50 text-indigo-600"
                        )}
                      >
                        <IconComponent className="h-4 w-4" />
                        <span>{tool.label}</span>
                      </Link>
                    </DropdownMenuItem>
                  );
                })}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Image Tools Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant={isImageToolActive ? "default" : "ghost"}
                  className={cn(
                    "flex items-center space-x-2 transition-all duration-200",
                    isImageToolActive && "bg-indigo-600 text-white shadow-md"
                  )}
                >
                  <Image className="h-4 w-4" />
                  <span className="hidden sm:inline">Image</span>
                  <ChevronDown className="h-3 w-3 ml-1" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                {imageTools.map((tool) => {
                  const IconComponent = tool.icon;
                  const isActive = location.pathname === tool.path;
                  
                  return (
                    <DropdownMenuItem key={tool.path} asChild>
                      <Link 
                        to={tool.path}
                        className={cn(
                          "flex items-center space-x-2 w-full px-3 py-2 transition-colors",
                          isActive && "bg-green-50 text-green-600"
                        )}
                      >
                        <IconComponent className="h-4 w-4" />
                        <span>{tool.label}</span>
                      </Link>
                    </DropdownMenuItem>
                  );
                })}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Text Tools Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant={isTextToolActive ? "default" : "ghost"}
                  className={cn(
                    "flex items-center space-x-2 transition-all duration-200",
                    isTextToolActive && "bg-indigo-600 text-white shadow-md"
                  )}
                >
                  <Type className="h-4 w-4" />
                  <span className="hidden sm:inline">Text</span>
                  <ChevronDown className="h-3 w-3 ml-1" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                {textTools.map((tool) => {
                  const IconComponent = tool.icon;
                  const isActive = location.pathname === tool.path;
                  
                  return (
                    <DropdownMenuItem key={tool.path} asChild>
                      <Link 
                        to={tool.path}
                        className={cn(
                          "flex items-center space-x-2 w-full px-3 py-2 transition-colors",
                          isActive && "bg-purple-50 text-purple-600"
                        )}
                      >
                        <IconComponent className="h-4 w-4" />
                        <span>{tool.label}</span>
                      </Link>
                    </DropdownMenuItem>
                  );
                })}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Jump to the extended tool catalogue on the homepage */}
            <Link to="/#json-tools" onClick={scrollToMoreTools}>
              <Button variant="ghost" className="flex items-center space-x-2 transition-all duration-200">
                <Wrench className="h-4 w-4" />
                <span className="hidden sm:inline">More Tools</span>
              </Button>
            </Link>

            {/* More Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  className="flex items-center space-x-2 transition-all duration-200"
                >
                  <MoreHorizontal className="h-4 w-4" />
                  <span className="hidden sm:inline">Help</span>
                  <ChevronDown className="h-3 w-3 ml-1" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuItem onClick={() => setFeedbackModalOpen(true)}>
                  <div className="flex items-center space-x-2 w-full px-3 py-2 cursor-pointer">
                    <MessageSquare className="h-4 w-4" />
                    <span>Feedback</span>
                  </div>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <a
                    href="https://litspark.solutions/?utm_source=allsmalltools"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center space-x-2 w-full px-3 py-2"
                  >
                    <ExternalLink className="h-4 w-4" />
                    <span>Visit LitSpark</span>
                  </a>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden py-4 border-t border-gray-200">
            <div className="flex flex-col space-y-2">
              {/* Home */}
              <Link 
                to="/" 
                onClick={() => setMobileMenuOpen(false)}
                className={cn(
                  "flex items-center space-x-2 px-4 py-2 rounded-md transition-colors",
                  location.pathname === '/' 
                    ? "bg-indigo-600 text-white" 
                    : "text-gray-700 hover:bg-gray-100"
                )}
              >
                <Home className="h-5 w-5" />
                <span>Home</span>
              </Link>

              {/* Online Notepad */}
              <Link 
                to="/online-notepad" 
                onClick={() => setMobileMenuOpen(false)}
                className={cn(
                  "flex items-center space-x-2 px-4 py-2 rounded-md transition-colors",
                  location.pathname === '/online-notepad' 
                    ? "bg-indigo-600 text-white" 
                    : "text-gray-700 hover:bg-gray-100"
                )}
              >
                <FileText className="h-5 w-5" />
                <span>Online Notepad</span>
              </Link>

              {/* PDF Tools */}
              <div className="space-y-1">
                <div className="px-4 py-2 text-sm font-semibold text-gray-500 uppercase tracking-wider">
                  PDF Tools
                </div>
                {pdfTools.map((tool) => {
                  const IconComponent = tool.icon;
                  const isActive = location.pathname === tool.path;
                  return (
                    <Link
                      key={tool.path}
                      to={tool.path}
                      onClick={() => setMobileMenuOpen(false)}
                      className={cn(
                        "flex items-center space-x-2 px-4 py-2 ml-4 rounded-md transition-colors",
                        isActive 
                          ? "bg-indigo-50 text-indigo-600" 
                          : "text-gray-700 hover:bg-gray-100"
                      )}
                    >
                      <IconComponent className="h-5 w-5" />
                      <span>{tool.label}</span>
                    </Link>
                  );
                })}
              </div>

              {/* Image Tools */}
              <div className="space-y-1">
                <div className="px-4 py-2 text-sm font-semibold text-gray-500 uppercase tracking-wider">
                  Image Tools
                </div>
                {imageTools.map((tool) => {
                  const IconComponent = tool.icon;
                  const isActive = location.pathname === tool.path;
                  return (
                    <Link
                      key={tool.path}
                      to={tool.path}
                      onClick={() => setMobileMenuOpen(false)}
                      className={cn(
                        "flex items-center space-x-2 px-4 py-2 ml-4 rounded-md transition-colors",
                        isActive 
                          ? "bg-green-50 text-green-600" 
                          : "text-gray-700 hover:bg-gray-100"
                      )}
                    >
                      <IconComponent className="h-5 w-5" />
                      <span>{tool.label}</span>
                    </Link>
                  );
                })}
              </div>

              {/* Text Tools */}
              <div className="space-y-1">
                <div className="px-4 py-2 text-sm font-semibold text-gray-500 uppercase tracking-wider">
                  Text Tools
                </div>
                {textTools.map((tool) => {
                  const IconComponent = tool.icon;
                  const isActive = location.pathname === tool.path;
                  return (
                    <Link
                      key={tool.path}
                      to={tool.path}
                      onClick={() => setMobileMenuOpen(false)}
                      className={cn(
                        "flex items-center space-x-2 px-4 py-2 ml-4 rounded-md transition-colors",
                        isActive 
                          ? "bg-purple-50 text-purple-600" 
                          : "text-gray-700 hover:bg-gray-100"
                      )}
                    >
                      <IconComponent className="h-5 w-5" />
                      <span>{tool.label}</span>
                    </Link>
                  );
                })}
              </div>

              <Link
                to="/#json-tools"
                onClick={() => {
                  setMobileMenuOpen(false);
                  scrollToMoreTools();
                }}
                className="flex items-center space-x-2 px-4 py-2 rounded-md text-gray-700 hover:bg-gray-100 transition-colors"
              >
                <Wrench className="h-5 w-5" />
                <span>More Tools</span>
              </Link>

              {/* More Section */}
              <div className="space-y-1">
                <div className="px-4 py-2 text-sm font-semibold text-gray-500 uppercase tracking-wider">
                  Help
                </div>
                <button
                  onClick={() => {
                    setFeedbackModalOpen(true);
                    setMobileMenuOpen(false);
                  }}
                  className="flex items-center space-x-2 px-4 py-2 ml-4 rounded-md transition-colors text-gray-700 hover:bg-gray-100 w-full text-left"
                >
                  <MessageSquare className="h-5 w-5" />
                  <span>Feedback</span>
                </button>
                <a
                  href="https://litspark.solutions/?utm_source=allsmalltools"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex items-center space-x-2 px-4 py-2 ml-4 rounded-md transition-colors text-gray-700 hover:bg-gray-100"
                >
                  <ExternalLink className="h-5 w-5" />
                  <span>Visit LitSpark</span>
                </a>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Feedback Modal */}
      <FeedbackModal 
        isOpen={feedbackModalOpen} 
        onClose={() => setFeedbackModalOpen(false)} 
      />
    </nav>
  );
};

export default Navbar;
